import React, { useState, useEffect, useCallback, memo } from 'react';
import axios from 'axios';

const API = (path) =>
    process.env.NODE_ENV === 'development'
        ? `http://localhost:5001/api${path}`
        : `/api${path}`;

const RECEIPT_FORMATS = [
    { value: 'standard', label: 'Standard Invoice (WB-XXXXXXX)' },
    { value: 'compact', label: 'Compact Receipt (XXXXXXX)' },
    { value: 'custom', label: 'Custom Prefix (e.g. CO-XXXX)' },
];

const AVAILABLE_MODULES = [
    { id: 'tracking', name: ' Tracking', description: 'Monitor shipments and delivery status' },
    { id: 'shipping', name: '📦 Export Label', description: 'For export shipments' },
    { id: 'bulk-shipping', name: '📑 Bulk Shipment', description: 'Bulk shipment via Excel' },
    { id: 'fc-label', name: '🏷️ Import Control Label', description: 'For import shipments' },
    { id: 'request-fc', name: '📋 Request Base FC Module', description: 'Request FC module access' },
    { id: 'rating', name: '💰 Rating', description: 'Calculate shipping rates and costs' },
    { id: 'search-zone', name: '🌍 Search Zone', description: 'Find shipping zones and locations' },
    { id: 'address-validation', name: '✅ Address Validation', description: 'Verify shipping addresses' },
    { id: 'settings', name: '⚙️ Settings', description: 'Configure application preferences' },
    { id: 'user-access', name: '👤 User Access', description: 'Manage user permissions' },
    { id: 'billing', name: '💵 Billing', description: 'View shipment history and costs' },
    { id: 'reports', name: '📊 Reporting Analytics', description: 'Reports & Business Intelligence' },
    { id: 'company', name: '🏢 Company Management', description: 'Manage multi-tenant companies' },
    { id: 'ops-security-scanning', name: '🛡️ OPS Security Scanning', description: 'Real-time barcode scanning and duplicate check' },
    { id: 'ops-receiving', name: '📦 OPS Receiving', description: 'Package receiving with barcode & label printing' },
];

// ─── FormField Component (outside main component to prevent re-renders) ───
const FormField = memo(({ label, children, tip, labelStyle }) => (
    <div style={{ marginBottom: '1.2rem' }}>
        <label style={labelStyle}>{label}</label>
        {children}
        {tip && <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#a0aec0' }}>{tip}</p>}
    </div>
));
FormField.displayName = 'FormField';

// ─── CompanyModal Component (outside main component to prevent re-renders) ───
const CompanyModal = memo(({ 
    title, onSave, saveBtnLabel, form, setForm, saving,
    setShowCreateModal, setSelectedCompany, resetForm,
    inputStyle, labelStyle, availableModules, toggleModule, availableAccounts, toggleAccount,
    availableImportAccounts, toggleImportAccount
}) => (
    <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        overflowY: 'auto', padding: '2rem 0'
    }}>
        <div style={{
            background: 'white', borderRadius: '20px', padding: '2.5rem',
            maxWidth: '600px', width: '94%', maxHeight: '98vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', margin: 'auto'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800, color: '#1a202c' }}>🏢 {title}</h3>
                <button onClick={() => { setShowCreateModal(false); setSelectedCompany(null); resetForm(); }}
                    style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' }}>×</button>
            </div>

            {/* Basic Info */}
            <div style={{ background: '#f7fafc', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                <p style={{ margin: '0 0 1rem', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', color: '#718096', letterSpacing: '1px' }}>
                    🏢 Company Information
                </p>
                <FormField label="Company Name *" labelStyle={labelStyle}>
                    <input
                        style={inputStyle}
                        placeholder="e.g. Acme Logistics Ltd"
                        autoComplete="off"
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                    />
                </FormField>
                <FormField label="Company Email * (used for notifications & login)" labelStyle={labelStyle}>
                    <input
                        style={inputStyle}
                        type="email"
                        placeholder="e.g. admin@company.com"
                        autoComplete="off"
                        value={form.email}
                        onChange={e => setForm({ ...form, email: e.target.value })}
                    />
                </FormField>
                <FormField label="Notes / Description" labelStyle={labelStyle}>
                    <textarea
                        style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
                        placeholder="Optional notes about this company..."
                        autoComplete="off"
                        value={form.notes}
                        onChange={e => setForm({ ...form, notes: e.target.value })}
                    />
                </FormField>
            </div>

            {/* UPS Credentials */}
            <div style={{ background: '#f0f4f8', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid #cbd5e0' }}>
                <p style={{ margin: '0 0 1rem', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', color: '#2d3748', letterSpacing: '1px' }}>
                    📦 UPS Credentials
                </p>
                <FormField label="UPS Client ID" labelStyle={labelStyle}>
                    <input
                        style={inputStyle}
                        placeholder="UPS Client ID"
                        autoComplete="off"
                        value={form.upsClientId}
                        onChange={e => setForm(p => ({ ...p, upsClientId: e.target.value }))}
                    />
                </FormField>
                <FormField label={`UPS Client Secret ${form.hasUpsClientSecret ? '(✅ Secret Set)' : '(❌ Not Set)'}`} labelStyle={labelStyle}>
                    <input
                        style={inputStyle}
                        type="password"
                        placeholder={form.hasUpsClientSecret ? 'Enter new secret to update or leave blank to keep existing' : 'UPS Client Secret'}
                        autoComplete="off"
                        value={form.upsClientSecret}
                        onChange={e => setForm(p => ({ ...p, upsClientSecret: e.target.value }))}
                    />
                </FormField>
                <FormField label="UPS Account Number" labelStyle={labelStyle}>
                    <input
                        style={inputStyle}
                        placeholder="UPS Account Number"
                        autoComplete="off"
                        value={form.upsAccountNumber}
                        onChange={e => setForm(p => ({ ...p, upsAccountNumber: e.target.value }))}
                    />
                </FormField>
                <p style={{ margin: '1rem 0 0', fontSize: '0.75rem', color: '#718096', fontStyle: 'italic' }}>
                    💡 These credentials are encrypted and used to create shipments for this company. The client ID will be displayed with only the last 4 digits visible in settings.
                </p>
            </div>

            {/* Module Access */}
            <div style={{ background: '#f7fafc', borderRadius: '12px', padding: '1.2rem', marginBottom: '1.5rem' }}>
                <p style={{ margin: '0 0 0.8rem', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', color: '#718096', letterSpacing: '1px' }}>
                    🔐 Module Access
                </p>
                <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#4a5568' }}>
                    Select which modules this company can use.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' }}>
                    {availableModules.map(module => (
                        <div
                            key={module.id}
                            onClick={() => toggleModule(module.id)}
                            style={{
                                padding: '0.6rem',
                                borderRadius: '6px',
                                border: form.modules?.includes(module.id) ? '2px solid #3182ce' : '1.5px solid #e2e8f0',
                                background: form.modules?.includes(module.id) ? '#ebf8ff' : '#fff',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#2d3748', lineHeight: '1.2' }}>{module.name}</div>
                            <div style={{ fontSize: '0.65rem', color: '#718096', marginTop: '2px', lineHeight: '1' }}>{module.description}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ICRIS Accounts Access */}
            <div style={{ background: '#f0f4f8', borderRadius: '12px', padding: '1.2rem', marginBottom: '1.5rem', border: '1px solid #cbd5e0' }}>
                <p style={{ margin: '0 0 0.8rem', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', color: '#2d3748', letterSpacing: '1px' }}>
                    📋 ICRIS Accounts Access
                </p>
                <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#4a5568' }}>
                    Select which UPS accounts this company can use.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' }}>
                    {availableAccounts.map(account => (
                        <div
                            key={account.value}
                            onClick={() => toggleAccount(account.value)}
                            style={{
                                padding: '0.6rem',
                                borderRadius: '6px',
                                border: form.accounts?.includes(account.value) ? '2px solid #38a169' : '1.5px solid #cbd5e0',
                                background: form.accounts?.includes(account.value) ? '#f0fff4' : '#fff',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                textAlign: 'center'
                            }}
                        >
                            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#2d3748' }}>{account.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Import Accounts Access (from Settings Import Tariffs) */}
            <div style={{ background: '#f0f9ff', borderRadius: '12px', padding: '1.2rem', marginBottom: '1.5rem', border: '1px solid #bae6fd' }}>
                <p style={{ margin: '0 0 0.8rem', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', color: '#0369a1', letterSpacing: '1px' }}>
                    ✈️ Import Accounts Access
                </p>
                <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#4a5568' }}>
                    Select which import (FC) accounts this company can use.
                </p>
                {availableImportAccounts && availableImportAccounts.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {availableImportAccounts.map(account => (
                            <div
                                key={account}
                                onClick={() => toggleImportAccount(account)}
                                style={{
                                    padding: '5px 12px',
                                    borderRadius: '20px',
                                    border: form.importAccounts?.includes(account) ? '2px solid #0284c7' : '1.5px solid #bae6fd',
                                    background: form.importAccounts?.includes(account) ? '#0284c7' : '#fff',
                                    color: form.importAccounts?.includes(account) ? '#fff' : '#0369a1',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    transition: 'all 0.2s'
                                }}
                            >
                                ✈️ {account}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>
                        No import accounts found. Add import tariffs in Settings to see accounts here.
                    </p>
                )}
            </div>

            {/* GL Account Ranges */}
            <div style={{ background: '#fffaf0', borderRadius: '12px', padding: '1.2rem', marginBottom: '1.5rem', border: '1px solid #cbd5e0' }}>
                <p style={{ margin: '0 0 0.8rem', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', color: '#2d3748', letterSpacing: '1px' }}>
                    ⚖️ GL Account Number Ranges
                </p>
                <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#4a5568' }}>
                    Configure GL account numbers mapped to specific weight ranges. These will automatically populate on shipments.
                </p>

                {form.glAccountRanges?.map((range, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                        <input
                            style={{ ...inputStyle, width: '130px', padding: '0.5rem' }}
                            placeholder="All Accounts"
                            value={range.upsAccount || ''}
                            onChange={e => { const n = [...(form.glAccountRanges || [])]; n[idx].upsAccount = e.target.value; setForm({ ...form, glAccountRanges: n }); }}
                        />
                        <input style={{ ...inputStyle, flex: 1, padding: '0.5rem' }} placeholder="GL Account" value={range.account} onChange={e => { const n = [...(form.glAccountRanges || [])]; n[idx].account = e.target.value; setForm({ ...form, glAccountRanges: n }); }} />
                        <input style={{ ...inputStyle, width: '80px', padding: '0.5rem' }} type="number" placeholder="Min" value={range.minWeight} onChange={e => { const n = [...(form.glAccountRanges || [])]; n[idx].minWeight = e.target.value; setForm({ ...form, glAccountRanges: n }); }} />
                        <span style={{ color: '#a0aec0' }}>-</span>
                        <input style={{ ...inputStyle, width: '80px', padding: '0.5rem' }} type="number" placeholder="Max" value={range.maxWeight} onChange={e => { const n = [...(form.glAccountRanges || [])]; n[idx].maxWeight = e.target.value; setForm({ ...form, glAccountRanges: n }); }} />
                        <button title="Remove Range" onClick={() => { const n = [...(form.glAccountRanges || [])]; n.splice(idx, 1); setForm({ ...form, glAccountRanges: n }); }} style={{ background: '#fed7d7', color: '#c53030', border: 'none', borderRadius: '4px', padding: '0.4rem 0.6rem', cursor: 'pointer', fontWeight: 'bold' }}>×</button>
                    </div>
                ))}

                <button onClick={() => setForm({ ...form, glAccountRanges: [...(form.glAccountRanges || []), { upsAccount: '', account: '', minWeight: '', maxWeight: '' }] })}
                    style={{ marginTop: '0.5rem', background: '#ebf4ff', color: '#2b6cb0', padding: '0.5rem 0.8rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                    + Add GL Account Range
                </button>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button onClick={onSave} disabled={saving}
                    style={{
                        flex: 1, padding: '0.85rem', borderRadius: '10px',
                        background: saving ? '#a0aec0' : 'linear-gradient(135deg, #667eea, #764ba2)',
                        color: 'white', fontWeight: 700, border: 'none', cursor: saving ? 'default' : 'pointer',
                        fontSize: '1rem', transition: 'all 0.2s'
                    }}>
                    {saving ? '⏳ Saving...' : saveBtnLabel}
                </button>
                <button onClick={() => { setShowCreateModal(false); setSelectedCompany(null); resetForm(); }}
                    style={{
                        flex: 0.4, padding: '0.85rem', borderRadius: '10px',
                        background: '#edf2f7', color: '#4a5568', fontWeight: 700, border: 'none', cursor: 'pointer'
                    }}>
                    Cancel
                </button>
            </div>
        </div>
    </div>
));
CompanyModal.displayName = 'CompanyModal';

const Company = ({ onBack, currentUser, isSuperUser }) => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [message, setMessage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [availableAccounts, setAvailableAccounts] = useState([]);
    const [availableImportAccounts, setAvailableImportAccounts] = useState([]);

    const [form, setForm] = useState({
        name: '',
        email: '',
        receiptEnabled: true,
        receiptStartNumber: '1000000',
        receiptFormat: 'standard',
        receiptPrefix: 'WB',
        notes: '',
        upsClientId: '',
        upsClientSecret: '',
        upsAccountNumber: '',
        modules: [],
        accounts: [],
        importAccounts: [],
        glAccountRanges: [],
        hasUpsClientSecret: false,
    });

    const showMsg = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 4000);
    };

    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(API('/companies'));
            if (res.data.success) setCompanies(res.data.companies);
        } catch (err) {
            showMsg('Failed to load companies', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAccounts = useCallback(async () => {
        try {
            const res = await axios.get(API('/accounts'));
            if (res.data.success) {
                const accounts = res.data.accounts || [];
                setAvailableAccounts(accounts.map(acc => ({ value: acc.accountNumber || acc, label: acc.accountNumber || acc })));
            }
        } catch (err) {
            console.error('Failed to load accounts:', err);
            setAvailableAccounts([]);
        }
    }, []);

    const fetchImportAccounts = useCallback(async () => {
        try {
            const res = await axios.get(API('/settings/import-tariffs'));
            if (res.data.success) {
                const importAccts = [...new Set((res.data.tariffs || []).map(t => t.account))].filter(Boolean).sort();
                setAvailableImportAccounts(importAccts);
            }
        } catch (err) {
            console.error('Failed to load import accounts:', err);
            setAvailableImportAccounts([]);
        }
    }, []);

    useEffect(() => {
        fetchCompanies();
        fetchAccounts();
        fetchImportAccounts();
    }, [fetchCompanies, fetchAccounts, fetchImportAccounts]);

    const resetForm = () => {
        setForm({
            name: '',
            email: '',
            receiptEnabled: true,
            receiptStartNumber: '1000000',
            receiptFormat: 'standard',
            receiptPrefix: 'WB',
            notes: '',
            upsClientId: '',
            upsClientSecret: '',
            upsAccountNumber: '',
            modules: [],
            accounts: [],
            importAccounts: [],
            glAccountRanges: [],
            hasUpsClientSecret: false,
        });
    };

    const validateEmail = (email) => {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail) return { valid: false, error: 'Email is required' };

        // Basic email validation
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
            return { valid: false, error: 'Please enter a valid email address' };
        }

        return { valid: true, normalizedEmail: normalizedEmail };
    };

    const toggleModule = (moduleId) => {
        const modules = form.modules || [];
        if (modules.includes(moduleId)) {
            setForm({ ...form, modules: modules.filter(m => m !== moduleId) });
        } else {
            setForm({ ...form, modules: [...modules, moduleId] });
        }
    };

    const toggleAccount = (accountId) => {
        const accounts = form.accounts || [];
        if (accounts.includes(accountId)) {
            setForm({ ...form, accounts: accounts.filter(a => a !== accountId) });
        } else {
            setForm({ ...form, accounts: [...accounts, accountId] });
        }
    };

    const toggleImportAccount = (accountId) => {
        const importAccounts = form.importAccounts || [];
        if (importAccounts.includes(accountId)) {
            setForm({ ...form, importAccounts: importAccounts.filter(a => a !== accountId) });
        } else {
            setForm({ ...form, importAccounts: [...importAccounts, accountId] });
        }
    };

    const handleCreate = async () => {
        if (!form.name.trim()) {
            showMsg('Company name is required', 'error');
            return;
        }

        const emailValidation = validateEmail(form.email);
        if (!emailValidation.valid) {
            showMsg(emailValidation.error, 'error');
            return;
        }

        setSaving(true);
        try {
            const res = await axios.post(API('/companies'), {
                ...form,
                email: emailValidation.normalizedEmail,
                createdBy: currentUser
            });
            if (res.data.success) {
                showMsg(`Company "${form.name}" created successfully! Database collection initialized.`);
                setShowCreateModal(false);
                resetForm();
                fetchCompanies();
            }
        } catch (err) {
            showMsg(err.response?.data?.message || 'Failed to create company', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async () => {
        if (!selectedCompany) return;

        const emailValidation = validateEmail(form.email);
        if (!emailValidation.valid) {
            showMsg(emailValidation.error, 'error');
            return;
        }

        setSaving(true);
        try {
            const res = await axios.put(API(`/companies/${selectedCompany._id}`), {
                ...form,
                email: emailValidation.normalizedEmail,
                updatedBy: currentUser
            });
            if (res.data.success) {
                showMsg('Company settings updated');
                setSelectedCompany(null);
                resetForm();
                fetchCompanies();
            }
        } catch (err) {
            showMsg(err.response?.data?.message || 'Failed to update company', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (company) => {
        if (!window.confirm(`Delete company "${company.name}"? This will remove all related data. This action cannot be undone.`)) return;
        try {
            await axios.delete(API(`/companies/${company._id}`));
            showMsg(`Company "${company.name}" deleted`);
            fetchCompanies();
        } catch (err) {
            showMsg(err.response?.data?.message || 'Failed to delete company', 'error');
        }
    };

    const openEdit = (company) => {
        setSelectedCompany(company);
        setForm({
            name: company.name || '',
            email: company.email || '',
            receiptEnabled: company.receiptEnabled !== false,
            receiptStartNumber: company.receiptStartNumber || '1000000',
            receiptFormat: company.receiptFormat || 'standard',
            receiptPrefix: company.receiptPrefix || 'WB',
            notes: company.notes || '',
            upsClientId: company.upsClientId || '',
            upsClientSecret: company.upsClientSecret || '',
            upsAccountNumber: company.upsAccountNumber || '',
            modules: company.modules || [],
            accounts: company.accounts || [],
            importAccounts: company.importAccounts || [],
            glAccountRanges: company.glAccountRanges || [],
            hasUpsClientSecret: !!company.hasUpsClientSecret,
        });
    };

    const cardStyle = {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        padding: '2rem',
        marginBottom: '1.5rem',
    };

    const badgeStyle = (color, bg) => ({
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: '20px',
        fontSize: '0.72rem',
        fontWeight: 700,
        background: bg,
        color: color,
        marginRight: '6px',
    });

    const inputStyle = {
        width: '100%',
        padding: '0.7rem 0.9rem',
        borderRadius: '8px',
        border: '1.5px solid #e2e8f0',
        fontSize: '0.95rem',
        background: '#f8fafc',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border 0.2s',
    };

    const labelStyle = {
        display: 'block',
        fontWeight: 600,
        fontSize: '0.85rem',
        color: '#4a5568',
        marginBottom: '0.4rem',
    };

    // ─── Render ────────────────────────────────────────────────────────────────
    return (
        <div style={{ padding: '2rem', maxWidth: '1100px', margin: '0 auto', fontFamily: "'Inter', sans-serif" }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem', gap: '1rem' }}>
                <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#4a5568' }}>←</button>
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, color: '#1a202c' }}>🏢 Company Management</h2>
                    <p style={{ margin: '4px 0 0', color: '#718096', fontSize: '0.9rem' }}>
                        Create and manage companies. Each company gets its own isolated data collection. Global settings apply to all companies.
                    </p>
                </div>
                <button onClick={() => { resetForm(); setShowCreateModal(true); }}
                    style={{
                        marginLeft: 'auto', padding: '0.75rem 1.5rem', borderRadius: '10px',
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        color: 'white', fontWeight: 700, border: 'none', cursor: 'pointer', fontSize: '0.95rem',
                        boxShadow: '0 4px 15px rgba(102,126,234,0.4)'
                    }}>
                    + New Company
                </button>
            </div>

            {/* Message */}
            {message && (
                <div style={{
                    padding: '1rem 1.5rem', borderRadius: '10px', marginBottom: '1.5rem',
                    background: message.type === 'success' ? '#c6f6d5' : '#fed7d7',
                    color: message.type === 'success' ? '#276749' : '#c53030',
                    fontWeight: 600, fontSize: '0.9rem'
                }}>
                    {message.type === 'success' ? '✅ ' : '❌ '}{message.text}
                </div>
            )}

            {/* Info Card */}
            <div style={{ ...cardStyle, background: 'linear-gradient(135deg, #667eea15, #764ba215)', border: '1px solid #667eea30', marginBottom: '2rem' }}>
                <h4 style={{ margin: '0 0 0.5rem', color: '#553c9a' }}>📋 ERP Architecture Overview</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                    {[
                        { icon: '🌍', title: 'Global Settings', desc: 'Fuel rates, conversion, peak charges — apply to ALL companies automatically' },
                        { icon: '🏢', title: 'Per-Company Data', desc: 'Each company has isolated shipments, billings, receipts, and addresses' },
                        { icon: '👑', title: 'Super Admin View', desc: 'Super users and admins with company access see all shipments across companies' },
                    ].map((item, i) => (
                        <div key={i} style={{ background: 'white', borderRadius: '10px', padding: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{item.icon}</div>
                            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#2d3748', marginBottom: '0.25rem' }}>{item.title}</div>
                            <div style={{ fontSize: '0.8rem', color: '#718096' }}>{item.desc}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Companies List */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '4rem', color: '#a0aec0' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                    <p>Loading companies...</p>
                </div>
            ) : companies.length === 0 ? (
                <div style={{ ...cardStyle, textAlign: 'center', padding: '4rem' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏢</div>
                    <h3 style={{ color: '#4a5568', margin: '0 0 0.5rem' }}>No Companies Yet</h3>
                    <p style={{ color: '#a0aec0', margin: '0 0 1.5rem' }}>Create your first company to start the multi-tenant ERP structure.</p>
                    <button onClick={() => setShowCreateModal(true)}
                        style={{
                            padding: '0.75rem 2rem', borderRadius: '10px',
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            color: 'white', fontWeight: 700, border: 'none', cursor: 'pointer'
                        }}>
                        + Create First Company
                    </button>
                </div>
            ) : (
                <div style={{ display: 'grid', gap: '1.2rem' }}>
                    {companies.map(company => (
                        <div key={company._id} style={{
                            ...cardStyle, padding: '1.5rem',
                            borderLeft: `5px solid ${company.receiptEnabled !== false ? '#667eea' : '#e53e3e'}`,
                            display: 'flex', alignItems: 'center', gap: '1.5rem'
                        }}>
                            <div style={{
                                width: '56px', height: '56px', borderRadius: '14px',
                                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '1.4rem', flexShrink: 0
                            }}>
                                🏢
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                    <span style={{ fontWeight: 800, fontSize: '1.05rem', color: '#1a202c' }}>{company.name}</span>
                                    {company.receiptEnabled !== false
                                        ? <span style={badgeStyle('#276749', '#c6f6d5')}>🧾 Receipts ON</span>
                                        : <span style={badgeStyle('#c53030', '#fed7d7')}>🚫 No Receipts</span>}
                                    <span style={badgeStyle('#553c9a', '#e9d8fd')}>
                                        {company.receiptFormat === 'custom' ? `${company.receiptPrefix}-XXXX` : company.receiptFormat || 'standard'}
                                    </span>
                                </div>
                                <div style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '4px' }}>
                                    📧 {company.email}
                                    {company.notes && <> &nbsp;|&nbsp; 📝 {company.notes}</>}
                                </div>
                                <div style={{ fontSize: '0.78rem', color: '#a0aec0', marginBottom: company.upsClientIdMasked ? '4px' : '0' }}>
                                    🗄️ Collection: <code style={{ background: '#f7fafc', padding: '1px 5px', borderRadius: '4px' }}>company_{company.slug}</code>
                                    &nbsp;|&nbsp; Created: {new Date(company.createdAt).toLocaleDateString()}
                                    {company.createdBy && <> &nbsp;by {company.createdBy}</>}
                                </div>
                                {company.upsClientIdMasked && (
                                    <div style={{ fontSize: '0.78rem', color: '#667eea', fontWeight: 600 }}>
                                        📦 UPS ID: {company.upsClientIdMasked}
                                        &nbsp;|&nbsp; Secret: {company.hasUpsClientSecret ? '✅ Set' : '❌ Not Set'}
                                        {company.upsAccountNumber && <> | Account: {company.upsAccountNumber}</>}
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                                <button onClick={() => openEdit(company)}
                                    style={{
                                        padding: '0.5rem 1rem', borderRadius: '8px',
                                        background: '#ebf4ff', color: '#2b6cb0', border: 'none',
                                        fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem'
                                    }}>
                                    ✏️ Edit
                                </button>
                                {isSuperUser && (
                                    <button onClick={() => handleDelete(company)}
                                        style={{
                                            padding: '0.5rem 1rem', borderRadius: '8px',
                                            background: '#fff5f5', color: '#c53030', border: 'none',
                                            fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem'
                                        }}>
                                        🗑️ Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <CompanyModal
                    title="Create New Company"
                    saveBtnLabel="🚀 Create Company & Initialize Database"
                    onSave={handleCreate}
                    form={form}
                    setForm={setForm}
                    saving={saving}
                    setShowCreateModal={setShowCreateModal}
                    setSelectedCompany={setSelectedCompany}
                    resetForm={resetForm}
                    inputStyle={inputStyle}
                    labelStyle={labelStyle}
                    availableModules={AVAILABLE_MODULES}
                    toggleModule={toggleModule}
                    availableAccounts={availableAccounts}
                    toggleAccount={toggleAccount}
                    availableImportAccounts={availableImportAccounts}
                    toggleImportAccount={toggleImportAccount}
                />
            )}

            {/* Edit Modal */}
            {selectedCompany && (
                <CompanyModal
                    title={`Edit: ${selectedCompany.name}`}
                    saveBtnLabel="💾 Save Company Settings"
                    onSave={handleUpdate}
                    form={form}
                    setForm={setForm}
                    saving={saving}
                    setShowCreateModal={setShowCreateModal}
                    setSelectedCompany={setSelectedCompany}
                    resetForm={resetForm}
                    inputStyle={inputStyle}
                    labelStyle={labelStyle}
                    availableModules={AVAILABLE_MODULES}
                    toggleModule={toggleModule}
                    availableAccounts={availableAccounts}
                    toggleAccount={toggleAccount}
                    availableImportAccounts={availableImportAccounts}
                    toggleImportAccount={toggleImportAccount}
                />
            )}
        </div>
    );
};

export default Company;
